import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import VideoLoader from "../../components/VideoLoader";
import { useNavigate } from "react-router-dom";
import LeftArrow from "../../assests/left-arrow.svg";
import LoyaltyBar from "../../components/Loyaltybar";
import api from "../../services";
import apple from "../../assests/apple.png";
import googlewallet from "../../assests/googlewallet.png";
import AppleWalletQRDialog from "../../components/AppleWalletQRDialog";
import toast from "react-hot-toast";
import {
  DiscountLevel,
  getDiscountLevels,
  getLoyaltyByPhone,
  LoyaltyPhoneData,
} from "../../services/apis";
import ErrorHandlerPage from "../../components/ErrorHandlerPage";

export default function LoyaltyPoints() {
  const { user, currency, currencySymbol } = useAuth();
  const navigate = useNavigate();
  const [points, setPoints] = useState<number>(0);
  const [discount, setDiscount] = useState<number | null>(null);
  const [levels, setLevels] = useState<DiscountLevel[]>([]);
  const [loyaltyData, setLoyaltyData] = useState<LoyaltyPhoneData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [pointsLoading, setPointsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [fatalError, setFatalError] = useState<{
    title: string;
    description: string;
  } | null>(null);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [userId, setUserId] = useState<string | undefined>();

  useEffect(() => {
    if(!user){
      navigate('/');
    }
    setUserId(user?.id);

  }, [user]);

  const handleDiscountChange = (newDiscount: number) =>
    setDiscount(newDiscount);

  const fetchLoyaltyData = useCallback(async () => {
    if (!user?.phonenumber) {
      console.error("User's phone number is not available");
      setPointsLoading(false);
      return;
    }
    try {
      setPointsLoading(true);
      const response = await getLoyaltyByPhone(user.phonenumber);
      const payload = response?.data;
      const data = payload?.data ?? payload;

      if (data) {
        setLoyaltyData(data);
        setPoints(data.points ?? 0);
      } else {
        setLoyaltyData(null);
        setPoints(0);
      }
    } catch (err) {
      // A missing/unavailable loyalty record (e.g. "Loyalty record not found")
      // must not block the page — show it with zero points instead.
      console.error("Failed to fetch loyalty data:", err);
      setLoyaltyData(null);
      setPoints(0);
    } finally {
      setPointsLoading(false);
    }
  }, [user?.phonenumber]);

  const getOS = () => {
    if (typeof window === "undefined") return "unknown";

    const ua = navigator.userAgent;

    if (/iPhone|iPad|iPod|Macintosh|Mac OS X/i.test(ua)) return "ios";
    if (/Android/i.test(ua)) return "android";

    return "other";
  };

  const os = getOS();

  const handleAddToWallet = async () => {
    try {
      if (!userId) return;

      const res = await api.get(`/wallet/qr/${userId}`);
      const passUrl = res.data.passUrl;

      // IMPORTANT: must be direct navigation
      window.location.href = passUrl;
    } catch (err) {
      console.error(err);
      toast.error("Failed to add pass to wallet");
    }
  };

  const fetchLevelsData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getDiscountLevels();
      const payload = response?.data;
      const list = Array.isArray(payload) ? payload : payload?.data;

      if (Array.isArray(list) && list.length > 0) {
        const sorted = list.sort(
          (a: DiscountLevel, b: DiscountLevel) => a.minPoints - b.minPoints,
        );
        setLevels(sorted);
        setError(null);
      } else {
        setError("No discounts available.");
      }
    } catch (err) {
      console.error("Failed to fetch loyalty levels:", err);
      setFatalError({
        title: "Loyalty Program Unavailable",
        description: "We are unable to load loyalty levels at the moment.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLevelsData();
  }, [fetchLevelsData]);

  useEffect(() => {
    if (user?.phonenumber) {
      fetchLoyaltyData();
    }
  }, [user, fetchLoyaltyData]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);

  const startDate = loyaltyData?.startDate
    ? new Date(loyaltyData.startDate)
    : null;
  const spentAmount = loyaltyData?.spendAmount ?? 0;
  const currentLevel = loyaltyData?.currentLevel;

  const daysPassed = startDate
    ? Math.max(
        0,
        Math.floor(
          (new Date().setHours(0, 0, 0, 0) -
            new Date(startDate).setHours(0, 0, 0, 0)) /
            (1000 * 60 * 60 * 24),
        ),
      )
    : 0;

  const daysLeftToRetain = currentLevel
    ? Math.max(currentLevel.minPointExpiryDays - daysPassed, 0)
    : 0;
  const daysLeftToUpgrade = currentLevel
    ? Math.max(currentLevel.maxPointExpiryDays - daysPassed, 0)
    : 0;

  const amountToRetain = currentLevel
    ? Math.max(currentLevel.minPoints - spentAmount, 0)
    : 0;
  const amountToUpgrade = currentLevel
    ? Math.max(currentLevel.maxPoints - spentAmount, 0)
    : 0;

  if (!user || loading || pointsLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <VideoLoader size="medium" />
      </div>
    );
  }

  if (fatalError) {
    return (
      <ErrorHandlerPage
        title={fatalError.title}
        description={fatalError.description}
        retryLabel="Retry"
        homeLabel="Back to Home"
        onHome={() => navigate("/")}
        onRetry={() => {
          setFatalError(null);
          fetchLevelsData();
          if (user?.phonenumber) {
            fetchLoyaltyData();
          }
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-white px-4 sm:px-6 py-4">
        <div className="max-w-4xl mx-auto px-2 sm:px-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              className="p-2 rounded-full items-center bg-transparent flex justify-center w-[35px] h-[35px] flex-shrink-0  border-[1px] border-black/20 text-black bg-white text-lg font-bold transition"
              onClick={() => navigate(-1)}
            >
              <img src={LeftArrow} alt="Back" className="w-4 h-4 dm-icon-invert" />
            </button>
            <h2 className="text-[18px] sm:text-[20px] font-bold m-0 text-black">
              Account Information
            </h2>
          </div>
        </div>
      </div>

      <div className="text-white p-4 sm:p-6">
        <div className="max-w-4xl mx-auto p-2 sm:p-4">
          {/* Profile Card */}
          <div className="text-black px-[12px] sm:px-[15px] py-[10px] bg-white border-[1px] border-black/5 rounded-2xl">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between w-full gap-4">
              {/* LEFT SIDE - Profile, Name & Loyalty Bar Combined */}
              <div className="flex items-center gap-3 sm:gap-4 w-full md:w-[50%]">
                {/* Profile Image */}
                  <div className="w-[60px] h-[60px] sm:w-[80px] sm:h-[80px] flex-shrink-0 rounded-full flex items-center justify-center text-2xl sm:text-3xl font-bold select-none" style={{ backgroundColor: 'var(--bw-black)', color: 'var(--bw-white)' }}>
                    {(user?.name || 'U').charAt(0).toUpperCase()}
                  </div>
              
                {/* Name and Loyalty Bar Column */}
                 <div className="flex flex-col gap-1 flex-1 min-w-0">
                  <h2 className="text-[18px] sm:text-[20px] font-bold mb-0">
                    {user?.name || "User"}
                  </h2>
                  <div className="w-full max-w-[500px] min-w-[50%]">
                    <LoyaltyBar
                      spendAmount={spentAmount}
                      currentLevel={currentLevel}
                      onDiscountChange={handleDiscountChange}
                      levels={levels}
                    />
                  </div>
                </div>
              </div>

              {/* RIGHT SIDE - Add to Apple Wallet Button */}
              <div className="w-full lg:w-auto">
                <button
                  onClick={handleAddToWallet}
                  className="flex items-center justify-center gap-2 px-4 py-2 border border-black text-black rounded-lg w-full lg:w-auto text-sm font-medium"
                >
                  <img
                    src={os === "ios" ? apple : googlewallet}
                    alt="Wallet"
                    className="w-7 h-7"
                  />
                  <span>
                    {os === "ios"
                      ? "Add Points to Apple Wallet"
                      : "Add Points to Google Wallet"}
                  </span>
                </button>
              </div>
            </div>

            {discount !== null && discount > 0 && (
              <div className="mt-4 pt-2 sm:pt-4 border-t border-black/5">
                <div className="flex sm:flex-row items-center sm:items-center justify-between bg-white px-3 py-3 rounded-lg">
                  <div>
                    <p className="text-[10px] m-0 sm:text-xs text-black/60 mb-1">
                      Your Current Discount
                    </p>
                    <p className="sm:text-2xl m-0 text-xl font-bold text-black">
                      {discount}% OFF
                    </p>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-[10px] m-0 sm:text-xs text-black/60 mb-1">
                      BonusWallet Balance
                    </p>
                    <p className="text-base m-0 sm:text-lg font-semibold text-black">
                      {currencySymbol}{(user?.bonusWallet?.balance ?? 0).toFixed(2)}
                    </p>
                    {(user?.bonusWallet?.pendingBalance ?? 0) > 0 && (
                      <p className="text-[10px] m-0 sm:text-xs text-amber-600">
                        + {currencySymbol}{(user?.bonusWallet?.pendingBalance ?? 0).toFixed(2)} pending
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {currentLevel && (
            <div className="mt-8 border border-black/5 rounded-2xl p-4 sm:p-6 bg-[#fff8eb] dm-loyalty-highlight">
              <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <p className="m-0 text-[12px] text-black/60">
                    Current Level
                  </p>
                  <h3 className="m-0 text-[24px] font-bold text-black">
                    {currentLevel.name}
                  </h3>
                  <p className="m-0 mt-2 text-[14px] text-black/80">
                    Spend {formatCurrency(amountToUpgrade)} more to move to next
                    level ({daysLeftToUpgrade} days left).
                  </p>
                  <p className="m-0 mt-1 text-[14px] text-black/80">
                    Spend minimum {formatCurrency(amountToRetain)} more to stay
                    in this level ({daysLeftToRetain} days left).
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 w-full lg:w-auto">
                  <div className="bg-white rounded-xl px-4 py-3 border border-black/5">
                    <p className="m-0 text-[12px] text-black/60">
                      Spent Amount
                    </p>
                    <p className="m-0 mt-1 text-[20px] font-bold text-black">
                      {formatCurrency(spentAmount)}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl px-4 py-3 border border-black/5">
                    <p className="m-0 text-[12px] text-black/60">
                      Discount
                    </p>
                    <p className="m-0 mt-1 text-[20px] font-bold text-black">
                      {currentLevel.discount}%
                    </p>
                  </div>
                  <div className="bg-white rounded-xl px-4 py-3 border border-black/5 col-span-2">
                    <p className="m-0 text-[12px] text-black/60">
                      Amount Earned
                    </p>
                    <p className="m-0 mt-1 text-[20px] font-bold text-black">
                      {currencySymbol}{(user?.bonusWallet?.balance ?? 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="max-w-4xl mx-auto  pt-0 pb-20">
            <div className="bg-white py-3 px-2 sm:px-3 border-[1px] border-black/5 rounded-2xl overflow-hidden mt-8">
              {levels.length === 0 && (
                <div className="px-[12px] sm:px-[15px] py-[20px] text-[14px] text-black/60">
                  {error || "No loyalty levels available."}
                </div>
              )}
              {levels.map((item, index) => (
                <div
                  key={index}
                  className="w-full flex items-center ap-3 gap-2 sm:gap-4 px-[12px] sm:px-[15px] py-[16px] sm:py-[20px]  transition border-b border-black/5 last:border-b-0"
                >
                  <div
                    className="p-2 w-[45px] h-[45px] border border-black/20
                                rounded-full flex items-center justify-center"
                    style={{ backgroundColor: item.color }}
                  ></div>

                  <div className="flex flex-col flex-1 text-left min-w-0">
                    <h3 className="font-bold text-[17px] sm:text-[19px] text-black mb-[2px]">
                      {item.name}
                    </h3>
                    <p className="text-[12px] sm:text-[13px] text-black/70 font-normal mb-0">
                      Spend {formatCurrency(item.minPoints)} -{" "}
                      {formatCurrency(item.maxPoints)}
                    </p>
                  </div>
                  <p className="text-lg sm:text-2xl font-bold text-black whitespace-nowrap m-0">
                    {item.discount}% Points
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <AppleWalletQRDialog
        userId={userId}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </div>
  );
}
